/**
 * Encaixes da casca (layout A + C) que as telas alimentam sem conhecer a casca.
 *
 * - `<ContextPanelSlot>`: conteúdo da tela no painel de contexto — grupos de facetas (`FacetFilters`) ou, em telas com
 *   hierarquia real, uma árvore (`replace`: o menu da seção começa recolhido e a árvore é o assunto do painel). Visões
 *   NÃO vão no painel: ficam em abas no topo do conteúdo. Vai por portal: a tela continua dona do estado. Com o painel
 *   recolhido ou em largura estreita, o conteúdo vai para a gaveta do painel (nunca vira bloco aberto no conteúdo).
 * - `useContextPanelState()`: se o painel está à vista e como abri-lo como gaveta (o botão "Filtros (n)" da
 *   `ListToolbar` usa); null fora da casca.
 * - `<HeaderActionsSlot>`: botões da tela no cabeçalho de 60 px, à direita da busca ("Ler agora", "Nova regra").
 * - `<BreadcrumbSlot>`: trechos a mais no caminho "Módulo / Tela" (ex.: o cluster aberto, "Openshift Sede").
 * - `useStatusBar(items, { updatedAt, what })`: itens do rodapé de status (ponto de estado + texto curto) e o
 *   "dados há N s". Itens são dados (texto), não nós React: a casca compara e só redesenha quando mudam.
 * - `useCommands(cmds)`: ações da tela na busca Ctrl K. Comando simples tem `run`; comando com argumento tem
 *   `parse(texto)`, que devolve a ação concreta quando o texto casa (ex.: "escalar aicentral-api para 4").
 *   Os comandos somem quando a tela fecha.
 *
 * Fora da casca (testes de tela, páginas soltas) os encaixes desenham no lugar e os ganchos não fazem nada.
 */
import { createContext, useCallback, useContext, useEffect, useMemo, useRef, useState, type ReactNode } from 'react';
import { createPortal } from 'react-dom';
import type { IconName } from './icons';

export type ShellSlotName = 'context' | 'actions' | 'crumbs';

/** O painel de contexto como a casca o mostra agora: na coluna (`visible`) ou recolhido (abre como gaveta). */
export interface ContextPanelState {
  visible: boolean;
  open: () => void;
}
export type StatusTone = 'ok' | 'warn' | 'crit' | 'info' | 'idle';

export interface StatusItem {
  id?: string;
  /** Texto curto: "4 coletores online", "412 pods · 14 com problema". */
  label: string;
  /** Cor do ponto à esquerda; sem tom, sem ponto. */
  tone?: StatusTone;
  /** Rota da console (abre ao clicar). */
  to?: string;
}

export interface StatusFeed {
  items: StatusItem[];
  /** Quando os dados da tela foram lidos: vira "dados há 18 s" (atualiza sozinho). */
  updatedAt?: number | string | Date | null;
  /** O que foi lido ("métricas", "inventário"): "métricas há 18 s". Sem ele, "dados". */
  what?: string;
}

export interface CommandAction {
  label: string;
  run: () => void | Promise<void>;
}

export interface ShellCommand {
  /** Identificador estável (o registro compara por ele). */
  id: string;
  /** Texto na lista da busca. */
  label: string;
  /** Forma de digitar, mostrada como dica ("escalar <carga> para <n>"). */
  hint?: string;
  /** Palavras que também acham o comando. */
  keywords?: string[];
  icon?: IconName;
  /** Comando simples. */
  run?: () => void | Promise<void>;
  /** Comando com argumento: a ação concreta quando o texto digitado casa, senão null. */
  parse?: (query: string) => CommandAction | null;
}

interface SlotsValue {
  targets: Partial<Record<ShellSlotName, HTMLElement | null>>;
  setTarget: (name: ShellSlotName, el: HTMLElement | null) => void;
  status: StatusFeed | null;
  putStatus: (owner: symbol, feed: StatusFeed | null) => void;
  commands: ShellCommand[];
  putCommands: (owner: symbol, cmds: ShellCommand[] | null) => void;
  /** Encaixes de painel montados pelas telas agora (e quantos substituem o menu por uma árvore). */
  ctx: { count: number; replace: number };
  putCtx: (delta: { count: number; replace: number }) => void;
  /** Trechos a mais no caminho montados agora (com algum, o trecho da tela vira link de volta). */
  crumbs: number;
  putCrumbs: (delta: number) => void;
  panel: ContextPanelState | null;
  setPanel: (p: ContextPanelState | null) => void;
}

const SlotsCtx = createContext<SlotsValue | null>(null);

/** Provedor da casca: guarda os alvos dos portais, o rodapé de status e os comandos registrados pelas telas. */
export function ShellSlotsProvider({ children }: { children: ReactNode }) {
  const [targets, setTargets] = useState<Partial<Record<ShellSlotName, HTMLElement | null>>>({});
  const [feeds, setFeeds] = useState<Array<[symbol, StatusFeed]>>([]);
  const [cmdLists, setCmdLists] = useState<Array<[symbol, ShellCommand[]]>>([]);
  const [ctx, setCtx] = useState({ count: 0, replace: 0 });
  const [panel, setPanel] = useState<ContextPanelState | null>(null);
  const [crumbs, setCrumbs] = useState(0);
  const putCrumbs = useCallback((d: number) => setCrumbs((c) => c + d), []);
  const putCtx = useCallback((d: { count: number; replace: number }) => setCtx((c) => ({ count: c.count + d.count, replace: c.replace + d.replace })), []);
  const setTarget = useCallback((name: ShellSlotName, el: HTMLElement | null) => {
    setTargets((t) => (t[name] === el ? t : { ...t, [name]: el }));
  }, []);
  const putStatus = useCallback((owner: symbol, feed: StatusFeed | null) => {
    setFeeds((l) => {
      const rest = l.filter(([o]) => o !== owner);
      return feed ? [...rest, [owner, feed]] : rest;
    });
  }, []);
  const putCommands = useCallback((owner: symbol, cmds: ShellCommand[] | null) => {
    setCmdLists((l) => {
      const rest = l.filter(([o]) => o !== owner);
      return cmds?.length ? [...rest, [owner, cmds]] : rest;
    });
  }, []);
  const value = useMemo<SlotsValue>(
    () => ({
      targets,
      setTarget,
      // vale o último que registrou (a tela aberta por cima de outra)
      status: feeds.length ? (feeds[feeds.length - 1]?.[1] ?? null) : null,
      putStatus,
      // os da tela mais recente primeiro
      commands: [...cmdLists].reverse().flatMap(([, c]) => c),
      putCommands,
      ctx,
      putCtx,
      crumbs,
      putCrumbs,
      panel,
      setPanel,
    }),
    [targets, setTarget, feeds, putStatus, cmdLists, putCommands, ctx, putCtx, crumbs, putCrumbs, panel],
  );
  return <SlotsCtx.Provider value={value}>{children}</SlotsCtx.Provider>;
}

/** Leitura da casca (só a casca precisa): alvos, rodapé e comandos. Null fora do provedor. */
export function useShellSlots(): SlotsValue | null {
  return useContext(SlotsCtx);
}

/** Ref de callback para a casca marcar onde cada encaixe desenha. */
export function useSlotTarget(name: ShellSlotName): (el: HTMLElement | null) => void {
  const ctx = useContext(SlotsCtx);
  const set = ctx?.setTarget;
  return useCallback((el: HTMLElement | null) => set?.(name, el), [set, name]);
}

/** Desenha os filhos no encaixe da casca; fora dela, no lugar. */
export function ShellSlot({ name, children }: { name: ShellSlotName; children: ReactNode }) {
  const ctx = useContext(SlotsCtx);
  if (!ctx) return <>{children}</>;
  const el = ctx.targets[name];
  return el ? createPortal(children, el) : null;
}

/** Conteúdo da tela no painel de contexto (grupos de facetas; com `replace`, a árvore da tela no lugar do menu). */
export function ContextPanelSlot({ children, replace }: { children: ReactNode; replace?: boolean }) {
  const ctx = useContext(SlotsCtx);
  const put = ctx?.putCtx;
  useEffect(() => {
    if (!put) return;
    const d = { count: 1, replace: replace ? 1 : 0 };
    put(d);
    return () => put({ count: -d.count, replace: -d.replace });
  }, [put, replace]);
  return <ShellSlot name="context">{children}</ShellSlot>;
}

/** O painel de contexto está à vista? (e como abri-lo como gaveta). Null fora da casca. */
export function useContextPanelState(): ContextPanelState | null {
  return useContext(SlotsCtx)?.panel ?? null;
}

/** Botões da tela no cabeçalho, à direita da busca. */
export function HeaderActionsSlot({ children }: { children: ReactNode }) {
  return <ShellSlot name="actions">{children}</ShellSlot>;
}

/**
 * Trechos a mais no caminho do cabeçalho (cada filho vira um "/ trecho"). Enquanto houver um, o trecho da tela
 * ("Nuvem" em "Nuvem / CAST GHANA") vira link de volta para a lista.
 */
export function BreadcrumbSlot({ children }: { children: ReactNode }) {
  const put = useContext(SlotsCtx)?.putCrumbs;
  useEffect(() => {
    if (!put) return;
    put(1);
    return () => put(-1);
  }, [put]);
  return <ShellSlot name="crumbs">{children}</ShellSlot>;
}

const stamp = (v: StatusFeed['updatedAt']) => (v == null ? '' : new Date(v).getTime());

/** Alimenta o rodapé de status da casca enquanto a tela está aberta. `null` limpa. */
export function useStatusBar(items: StatusItem[] | null, opts: { updatedAt?: StatusFeed['updatedAt']; what?: string } = {}) {
  const ctx = useContext(SlotsCtx);
  const put = ctx?.putStatus;
  const owner = useRef(Symbol('status')).current;
  const sig = items ? JSON.stringify([items.map((i) => [i.id ?? '', i.label, i.tone ?? '', i.to ?? '']), stamp(opts.updatedAt), opts.what ?? '']) : '';
  const latest = useRef<StatusFeed | null>(null);
  latest.current = items ? { items, updatedAt: opts.updatedAt, what: opts.what } : null;
  useEffect(() => {
    if (!put) return;
    put(owner, latest.current);
  }, [put, owner, sig]);
  useEffect(() => () => put?.(owner, null), [put, owner]);
}

/** Registra comandos da tela na busca Ctrl K; somem quando a tela fecha. */
export function useCommands(cmds: ShellCommand[]) {
  const ctx = useContext(SlotsCtx);
  const put = ctx?.putCommands;
  const owner = useRef(Symbol('commands')).current;
  const latest = useRef(cmds);
  latest.current = cmds;
  const sig = cmds.map((c) => [c.id, c.label, c.hint ?? '', (c.keywords ?? []).join(','), c.icon ?? '', c.run ? 1 : 0, c.parse ? 1 : 0].join('|')).join('\n');
  useEffect(() => {
    if (!put) return;
    // os registrados chamam sempre a versão mais recente do comando (o fechamento da tela muda a cada desenho)
    const find = (id: string) => latest.current.find((c) => c.id === id);
    put(
      owner,
      latest.current.map((c) => ({
        ...c,
        run: c.run ? () => find(c.id)?.run?.() : undefined,
        parse: c.parse ? (q: string) => find(c.id)?.parse?.(q) ?? null : undefined,
      })),
    );
  }, [put, owner, sig]);
  useEffect(() => () => put?.(owner, null), [put, owner]);
}

/** Comandos que casam com o texto: os com argumento viram a ação concreta; os simples casam por rótulo e palavras. */
export function matchCommands(cmds: ShellCommand[], query: string): Array<{ id: string; label: string; hint?: string; icon?: IconName; run: () => void | Promise<void> }> {
  const q = norm(query.trim());
  const out: Array<{ id: string; label: string; hint?: string; icon?: IconName; run: () => void | Promise<void> }> = [];
  for (const c of cmds) {
    if (c.parse && q) {
      const a = c.parse(query.trim());
      if (a) {
        out.push({ id: c.id, label: a.label, hint: c.hint, icon: c.icon, run: a.run });
        continue;
      }
    }
    if (!c.run) continue;
    const hay = norm([c.label, ...(c.keywords ?? [])].join(' '));
    if (!q || q.split(/\s+/).every((w) => hay.includes(w))) out.push({ id: c.id, label: c.label, hint: c.hint, icon: c.icon, run: c.run });
  }
  return out;
}

/** Sem acento e em minúsculas, para casar "seguranca" com "Segurança". */
export function norm(s: string): string {
  return s.normalize('NFD').replace(/[̀-ͯ]/g, '').toLowerCase();
}
